import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import LoadingWithHelp from '../ui/LoadingWithHelp';
import { 
  clearCheckoutState,
  isReturningFromStripe,
  isReturningFromPortal, 
  clearPortalState 
} from '../../utils/checkoutStorage';
import { getPasswordRecoveryEmail, isPasswordRecoveryPending } from '../../utils/passwordRecoveryStorage';
import {
  getOAuthCallbackErrorFromUrl,
  hasOAuthExchangePendingInUrl,
} from '../../utils/authRedirect';
import { isLoggingOutFlagSet } from '../../utils/authReset';

interface SimpleProtectedRouteProps {
  children: React.ReactNode;
}

const OAUTH_CALLBACK_WAIT_MS = 12000;

export const SimpleProtectedRoute: React.FC<SimpleProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const { isLanguageReady } = useI18n();
  const location = useLocation();
  const [oauthWaitExpired, setOauthWaitExpired] = useState(false);

  // PKCE: wait for code exchange, but never forever (failed/hung exchange → login).
  useEffect(() => {
    if (user || loading || getOAuthCallbackErrorFromUrl() || !hasOAuthExchangePendingInUrl()) {
      setOauthWaitExpired(false);
      return;
    }
    const timer = window.setTimeout(() => setOauthWaitExpired(true), OAUTH_CALLBACK_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [user, loading, location.pathname, location.search, location.hash]);

  // Recovery OTP grants a temporary session — finish reset before entering the app
  if (!loading && user && isPasswordRecoveryPending()) {
    const recoveryEmail = getPasswordRecoveryEmail();
    const query = recoveryEmail ? `?email=${encodeURIComponent(recoveryEmail)}` : '';
    return <Navigate to={`/reset-password${query}`} replace />;
  }

  // Standard SaaS: Check if user is returning from Stripe checkout (cancelled) or billing portal
  // BEFORE rendering to prevent flash
  if (!loading && user) {
    // Stripe checkout browser-back return: always normalize to pricing cancelled so toast is shown.
    if (isReturningFromStripe()) {
      clearCheckoutState();
      return <Navigate to="/pricing?checkout=cancelled" replace />;
    }

    // Check for billing portal return
    if (isReturningFromPortal() && location.pathname !== '/app/settings') {
      clearPortalState();
      return <Navigate to="/app/settings?tab=subscription" replace />;
    }
  }

  // Wait for auth to be ready first
  if (loading) {
    return <LoadingWithHelp loading={true} fullScreen={true} />;
  }

  // If not authenticated, redirect immediately (no need to wait for language)
  if (!user) {
    // During logout we hard-navigate to "/", but auth state can transiently become null first.
    // Avoid flashing /login by redirecting to landing when a logout is in progress.
    if (isLoggingOutFlagSet()) {
      return <Navigate to="/" replace />;
    }
    // Provider returned an error in the URL — fail fast (no 12s PKCE wait).
    if (getOAuthCallbackErrorFromUrl()) {
      const redirect = encodeURIComponent(location.pathname || '/app');
      return <Navigate to={`/login?oauth_error=1&redirect=${redirect}`} replace />;
    }
    // Google/OAuth PKCE: code is still in the URL — wait for exchange, do not strip it via /login.
    if (hasOAuthExchangePendingInUrl() && !oauthWaitExpired) {
      return <LoadingWithHelp loading={true} fullScreen={true} />;
    }
    // Drop OAuth query/hash from the redirect target so a failed exchange cannot loop.
    const redirect = encodeURIComponent(location.pathname || '/app');
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  // User is authenticated - wait for language to be ready before showing content
  // This prevents showing translation keys while loading user's language preference
  if (!isLanguageReady) {
    return <LoadingWithHelp loading={true} fullScreen={true} />;
  }

  // With OTP, user is automatically verified after OTP verification
  // No need to check email_confirmed_at anymore
  return <>{children}</>;
};
